import { cva } from '@vizit/theme';

/**
 * Buttons / Hover Action (spec): icon-only square button, 5 sizes × 3
 * hierarchies × interaction states. State classes are resolved from
 * react-aria render props, so exactly one background wins per state
 * (tailwind-merge keeps the last conflicting class; compound variants are
 * emitted after base variants, and `isDisabled` is declared after
 * `hierarchy` so the disabled treatment overrides the hierarchy's). The same
 * holds for shadows only because `@vizit/theme` registers the custom
 * `boxShadow` names with tailwind-merge, so `shadow-hover-action-button-focused`
 * replaces the primary hierarchy's `shadow-xs` instead of stacking under it.
 */
const hoverActionButtonClasses = cva({
  base: ['flex items-center justify-center rounded-none p-0 outline-none', 'transition-colors duration-150'],
  variants: {
    size: {
      xs: 'h-14 w-14',
      sm: 'h-18 w-18',
      md: 'h-20 w-20',
      lg: 'h-22 w-22',
      xl: 'h-24 w-24',
    },
    hierarchy: {
      primary: 'bg-primary/90 text-tertiary shadow-xs',
      secondary: 'bg-overlay-action text-quinary',
      destructive: 'bg-overlay-action text-quinary',
    },
    isDisabled: {
      true: 'cursor-not-allowed border border-disabled bg-primary-disabled text-disabled shadow-none',
      false: 'cursor-pointer',
    },
    isFocusVisible: {
      true: 'shadow-hover-action-button-focused',
      false: '',
    },
    isHovered: {
      true: '',
      false: '',
    },
    isLoading: {
      true: 'pointer-events-none',
      false: '',
    },
  },
  compoundVariants: [
    // The hover/focus compounds are gated on isDisabled: false — compounds are
    // emitted after base variants, so ungated they would override the disabled
    // bg/text, and disabled buttons still reach both states: the tooltip hook
    // keeps them tabbable and hoverable. Loading compounds are deliberately
    // ungated (loading wins over disabled, see the loading+disabled test).
    { hierarchy: 'primary', isHovered: true, isDisabled: false, className: 'bg-primary' },
    { hierarchy: 'primary', isLoading: true, className: 'bg-primary' },
    // Gated on !isFocusVisible for the same reason: an ungated shadow-xs
    // would override the focus ring, the only focus indicator.
    { hierarchy: 'primary', isDisabled: true, isFocusVisible: false, className: 'shadow-xs' },
    { hierarchy: 'secondary', isHovered: true, isDisabled: false, className: 'bg-primary text-tertiary' },
    { hierarchy: 'secondary', isFocusVisible: true, isDisabled: false, className: 'bg-primary text-tertiary' },
    { hierarchy: 'secondary', isLoading: true, className: 'bg-primary' },
    {
      hierarchy: 'destructive',
      isHovered: true,
      isDisabled: false,
      className: 'bg-error-strong text-primary-on-brand',
    },
    {
      hierarchy: 'destructive',
      isFocusVisible: true,
      isDisabled: false,
      className: 'bg-error-strong text-primary-on-brand',
    },
    { hierarchy: 'destructive', isLoading: true, className: 'bg-error-strong' },
  ],
  defaultVariants: {
    size: 'sm',
    hierarchy: 'primary',
    isDisabled: false,
    isFocusVisible: false,
    isHovered: false,
    isLoading: false,
  },
});

export { hoverActionButtonClasses };
